import { useEffect, useRef, useState } from "react";
import type { DecisionSummary, ServiceId } from "../../types/domain";
import {
  AMAP_CHARGING_DESTINATION,
  type ChargingStation,
  type ChargingStationSource,
} from "../../config/amapChargingDemo";
import type { CockpitCarwashData, CockpitFuelData, CockpitParcelData } from "../../context/CockpitDataContext";
import { getNightAiCardContent, idleNightAiCardContent } from "../../config/nightAiCardContent";
import { NightAiScenarioIcon } from "./NightAiScenarioIcon";
import "./NightCards.css";

interface NightAiCardProps {
  summary: DecisionSummary;
  selectedService: ServiceId | null;
  chargingStation?: ChargingStation;
  chargingStationCount?: number;
  chargingStationSource?: ChargingStationSource;
  destinationName?: string;
  carwash?: CockpitCarwashData;
  fuel?: CockpitFuelData;
  parcel?: CockpitParcelData;
  onOpenService: (serviceId: ServiceId) => void;
}

export function NightAiCard({
  summary,
  selectedService,
  chargingStation,
  chargingStationCount = 0,
  chargingStationSource = "loading",
  destinationName = AMAP_CHARGING_DESTINATION.name,
  carwash,
  fuel,
  parcel,
  onOpenService
}: NightAiCardProps) {
  const decision = summary.activeDecision;
  const scenario = decision?.scenario ?? null;
  const content = scenario ? getNightAiCardContent(scenario.id) : idleNightAiCardContent;
  const isIdle = !scenario;
  const isCharging = scenario?.id === "charging" && chargingStation;
  const carwashFact = scenario?.id === "carwash" ? carwash : undefined;
  const fuelFact = scenario?.id === "refuel" ? fuel : undefined;
  const parcelFact = scenario?.id === "parcel" ? parcel : undefined;
  const cardDescription = isCharging
    ? buildChargingCollapsedDescription(chargingStation, chargingStationSource, destinationName)
    : carwashFact
    ? buildCarwashCollapsedDescription(carwashFact)
    : fuelFact
    ? buildFuelCollapsedDescription(fuelFact)
    : parcelFact
    ? buildParcelCollapsedDescription(parcelFact)
    : content.collapsedDescription;
  const scenarioKey = scenario?.id ?? "idle";
  const previousScenarioKeyRef = useRef(scenarioKey);
  const [isWakeMotionActive, setIsWakeMotionActive] = useState(false);

  useEffect(() => {
    if (previousScenarioKeyRef.current === scenarioKey) {
      return undefined;
    }

    previousScenarioKeyRef.current = scenarioKey;

    if (isIdle) {
      setIsWakeMotionActive(false);
      return undefined;
    }

    setIsWakeMotionActive(true);
    const timeoutId = window.setTimeout(() => {
      setIsWakeMotionActive(false);
    }, 980);

    return () => window.clearTimeout(timeoutId);
  }, [isIdle, scenarioKey]);

  return (
    <button
      type="button"
      className={[
        "night-ai-card",
        isIdle ? "night-ai-card--idle" : "",
        !isIdle ? "night-ai-card--active" : "",
        isWakeMotionActive ? "night-ai-card--wake-motion" : "",
        scenario && selectedService === scenario.id ? "night-ai-card--selected" : ""
      ].filter(Boolean).join(" ")}
      aria-label={scenario ? `打开${scenario.title}` : "服务待命"}
      aria-pressed={scenario ? selectedService === scenario.id : false}
      disabled={isIdle}
      onClick={() => {
        if (scenario) {
          onOpenService(scenario.id);
        }
      }}
    >
      <span className="night-ai-card__surface" aria-hidden="true" />
      <span className={`night-ai-scenario-icon night-ai-scenario-icon--${content.icon}`}>
        <NightAiScenarioIcon icon={content.icon} />
      </span>
      <span className="night-ai-card__copy">
        <span className="night-ai-card__title">{content.collapsedTitle}</span>
        <span className="night-ai-card__description">{cardDescription}</span>
      </span>
      <span className="night-card__sr-copy">{summary.generatedCopy}</span>
    </button>
  );
}

function buildChargingCollapsedDescription(
  station: ChargingStation,
  source: ChargingStationSource,
  destinationName: string
) {
  if (source === "loading") {
    return `${destinationName}附近有充电站，电价更新中`;
  }

  const { chargePriceLabel } = buildChargingPriceFields(station);
  return `${destinationName}附近有充电站，距你约${formatRouteDistance(station.routeDistanceKm)}，${chargePriceLabel}`;
}

function buildChargingPriceFields(station: ChargingStation) {
  return {
    chargePriceLabel: `${formatPrice(station.pricePerKwh)}元/度`,
    parkingFeeLabel: station.parkingFee
  };
}

function formatPrice(price: number) {
  return Number.isInteger(price) ? String(price) : price.toFixed(2).replace(/0$/, "");
}

function formatRouteDistance(distanceKm: number) {
  return distanceKm < 1 ? `${Math.round(distanceKm * 1000)}m` : `${distanceKm.toFixed(1)}km`;
}

function buildParcelCollapsedDescription(parcel: CockpitParcelData) {
  if (parcel.pendingCount <= 0) {
    return "暂无待取包裹";
  }

  return `${parcel.pendingCount}件待取，家附近${parcel.pickupPoint.name}${parcel.onRoute ? "可导航" : "待同步"}`;
}

function buildCarwashCollapsedDescription(carwash: CockpitCarwashData) {
  if (carwash.source === "loading") {
    return "高德正在筛选顺路洗车门店";
  }

  const shop = carwash.recommendedShop;
  return `${shop.shortName || shop.name}，${shop.priceLabel}，等待${shop.waitMinutes}分钟`;
}

function buildFuelCollapsedDescription(fuel: CockpitFuelData) {
  if (fuel.source === "loading") {
    return "高德正在筛选顺路加油站";
  }

  const station = fuel.recommendedStation;
  return `${station.shortName || station.name}，${station.gradeLabel}，${station.amountLabel}`;
}
